/**
 * ExerciseMediaProvider — resolves media URLs for Move exercises.
 *
 * Resolution order:
 *   1. `video_url` / `thumbnail_url` set from the Admin panel (R2 storage,
 *      served via /api/files/…) — prefixed with the backend URL.
 *   2. Local bundle fallback: /workouts/{slug}.mp4 and
 *      /workouts/thumbs/{slug}.jpg (drop the MP4 files in
 *      frontend/public/workouts/ to make them instantly available).
 *
 * Videos are lazy-loaded by the player; `preloadExerciseVideo` warms the
 * HTTP cache for the NEXT exercise only.
 */
import { API_URL } from '../config/api';

const absolutize = (url) => {
  if (!url) return null;
  if (url.startsWith('/api')) return `${API_URL}${url}`;
  return url;
};

export const getExerciseVideoUrl = (exercise) => {
  if (!exercise) return null;
  if (exercise.video_url) return absolutize(exercise.video_url);
  return exercise.slug ? `/workouts/${exercise.slug}.mp4` : null;
};

export const getExerciseThumbUrl = (exercise) => {
  if (!exercise) return null;
  if (exercise.thumbnail_url) return absolutize(exercise.thumbnail_url);
  return exercise.slug ? `/workouts/thumbs/${exercise.slug}.jpg` : null;
};

const preloaded = new Set();

/** Warm the browser cache for the next exercise's video (lazy strategy). */
export const preloadExerciseVideo = (exercise) => {
  const url = getExerciseVideoUrl(exercise);
  if (!url || preloaded.has(url)) return;
  preloaded.add(url);
  try {
    const v = document.createElement('video');
    v.preload = 'auto';
    v.muted = true;
    v.src = url;
    v.load();
  } catch (_) { /* noop */ }
};

/** Pick the localized value of a {it,en,es,pt,de,fr} dict. */
export const localized = (dict, language) => {
  if (!dict) return '';
  return dict[language] || dict.it || dict.en || Object.values(dict)[0] || '';
};
